import { useState, useEffect } from 'react';
import { Award, Calendar, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import Card from './ui/Card';
import Alert from './ui/Alert';
import LoadingSpinner from './ui/LoadingSpinner';
import stateLicenseService from '../services/unifiedStateLicenseService';
import { getStateLicenses } from '../data/interpreterLicensureRequirements';
import dayjs from 'dayjs';

export default function StateLicenseProgress({ userId, userProfile, currentCycleCEUs }) {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userId) {
      loadStateLicenses();
    }
  }, [userId]);

  const loadStateLicenses = async () => {
    try {
      const result = await stateLicenseService.getUserStateLicenses(userId);
      if (result.success) {
        setLicenses(result.licenses);
      } else {
        setError('Failed to load state licenses');
      }
    } catch (error) {
      setError('Failed to load state licenses');
    } finally {
      setLoading(false);
    }
  };

  const calculateLicenseProgress = (license) => {
    // Get state license requirements
    const stateLicenses = getStateLicenses(license.state);
    const licenseType = stateLicenses.find(l => l.name === license.licenseType);
    
    if (!licenseType) {
      return null;
    }

    // Parse CEU requirements
    const ceuRequirements = licenseType.ceuRequirements;
    let requiredCEUs = 0;
    let specialRequirements = [];

    // Parse different requirement formats
    if (ceuRequirements.includes('Maintain RID')) {
      // Follows RID requirements
      requiredCEUs = 8.0; // Standard RID requirement
      specialRequirements.push('Must maintain RID certification');
    } else if (ceuRequirements.includes('20 hours legal')) {
      // Legal specialty requirements
      requiredCEUs = 8.0; // Base RID + legal
      specialRequirements.push('20 hours legal CEUs per cycle');
    } else if (ceuRequirements.includes('Follow RID')) {
      requiredCEUs = 8.0;
      specialRequirements.push('Follow RID requirements');
    }

    // Calculate current progress
    const totalCEUs = currentCycleCEUs.reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);
    
    // Calculate legal CEUs if required
    let legalCEUs = 0;
    if (specialRequirements.some(req => req.includes('legal'))) {
      legalCEUs = currentCycleCEUs
        .filter(ceu => 
          ceu.subCategory?.toLowerCase().includes('legal') ||
          ceu.category?.toLowerCase().includes('legal') ||
          ceu.title?.toLowerCase().includes('legal')
        )
        .reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);
    }

    // Calculate renewal status
    const renewalDate = license.expirationDate ? dayjs(license.expirationDate) : null;
    const daysUntilRenewal = renewalDate ? renewalDate.diff(dayjs(), 'day') : null;
    
    let renewalStatus = 'unknown';
    if (daysUntilRenewal !== null) {
      if (daysUntilRenewal < 0) {
        renewalStatus = 'expired';
      } else if (daysUntilRenewal <= 30) {
        renewalStatus = 'urgent';
      } else if (daysUntilRenewal <= 90) {
        renewalStatus = 'warning';
      } else {
        renewalStatus = 'good';
      }
    }

    return {
      requiredCEUs,
      totalCEUs,
      legalCEUs,
      specialRequirements,
      renewalDate,
      daysUntilRenewal,
      renewalStatus,
      auditCycle: licenseType.auditCycle,
      isComplete: totalCEUs >= requiredCEUs,
      progress: requiredCEUs > 0 ? (totalCEUs / requiredCEUs) * 100 : 0
    };
  };

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-4" role="status" aria-live="polite">
          <LoadingSpinner size="sm" />
          <span className="sr-only">Loading state license progress information</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert variant="error" role="alert" aria-live="assertive">{error}</Alert>
      </Card>
    );
  }

  if (licenses.length === 0) {
    return null; // Don't show anything if no licenses
  }

  return (
    <section className="space-y-4" aria-labelledby="state-license-heading">
      <h2 id="state-license-heading" className="sr-only">State License Progress</h2>
      {licenses.map((license) => {
        const progress = calculateLicenseProgress(license);
        
        if (!progress) {
          return (
            <Card key={license.licenseId}>
              <div className="flex items-center gap-3" role="status">
                <Award className="w-5 h-5 text-gray-400" aria-hidden="true" />
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {license.state} - {license.licenseType}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Requirements not available - check with your state board
                  </p>
                </div>
              </div>
            </Card>
          );
        }

        const getStatusIcon = () => {
          const iconProps = { className: "w-5 h-5", "aria-hidden": "true" };
          switch (progress.renewalStatus) {
            case 'expired':
              return <AlertTriangle {...iconProps} className="w-5 h-5 text-red-500" />;
            case 'urgent':
              return <AlertTriangle {...iconProps} className="w-5 h-5 text-orange-500" />;
            case 'warning':
              return <Clock {...iconProps} className="w-5 h-5 text-yellow-500" />;
            case 'good':
              return <CheckCircle {...iconProps} className="w-5 h-5 text-green-500" />;
            default:
              return <Award {...iconProps} className="w-5 h-5 text-primary" />;
          }
        };

        const getStatusColor = () => {
          switch (progress.renewalStatus) {
            case 'expired':
              return 'text-red-600';
            case 'urgent':
              return 'text-orange-600';
            case 'warning':
              return 'text-yellow-600';
            case 'good':
              return 'text-green-600';
            default:
              return 'text-gray-600';
          }
        };

        const getStatusText = () => {
          switch (progress.renewalStatus) {
            case 'expired':
              return 'Expired';
            case 'urgent':
              return 'Urgent renewal required';
            case 'warning':
              return 'Renewal needed soon';
            case 'good':
              return 'License in good standing';
            default:
              return 'Status unknown';
          }
        };

        return (
          <Card key={license.licenseId}>
            <article className="space-y-4" role="region" aria-labelledby={`license-${license.licenseId}-title`}>
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {getStatusIcon()}
                  <div>
                    <h3 id={`license-${license.licenseId}-title`} className="font-semibold text-gray-900">
                      {license.state} - {license.licenseType}
                    </h3>
                    {license.licenseNumber && (
                      <p className="text-sm text-gray-600">
                        License #: {license.licenseNumber}
                      </p>
                    )}
                    {/* Screen reader status announcement */}
                    <span className="sr-only">
                      License status: {getStatusText()}
                    </span>
                  </div>
                </div>
                
                {progress.renewalDate && (
                  <div className={`text-right ${getStatusColor()}`} role="status" aria-live="polite">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" aria-hidden="true" />
                      <span className="text-sm font-medium">
                        {progress.renewalStatus === 'expired' ? 'Expired' : 
                         progress.daysUntilRenewal <= 30 ? `${progress.daysUntilRenewal} days` :
                         progress.renewalDate.format('MMM YYYY')}
                      </span>
                    </div>
                    <p className="text-xs">
                      {progress.renewalStatus === 'expired' ? 
                        progress.renewalDate.format('MMM D, YYYY') :
                        'Renewal due'
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              <div role="group" aria-labelledby={`progress-${license.licenseId}-label`}>
                <div className="flex items-center justify-between mb-2">
                  <span id={`progress-${license.licenseId}-label`} className="text-sm font-medium text-gray-700">
                    CEU Progress
                  </span>
                  <span className="text-sm text-gray-600" aria-label={`${progress.totalCEUs.toFixed(1)} of ${progress.requiredCEUs.toFixed(1)} CEUs completed`}>
                    {progress.totalCEUs.toFixed(1)} / {progress.requiredCEUs.toFixed(1)} CEUs
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" role="progressbar" 
                     aria-valuenow={Math.round(progress.progress)} 
                     aria-valuemin="0" 
                     aria-valuemax="100"
                     aria-labelledby={`progress-${license.licenseId}-label`}
                     aria-describedby={`progress-${license.licenseId}-description`}>
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      progress.isComplete ? 'bg-green-500' : 'bg-primary'
                    }`}
                    style={{ width: `${Math.min(progress.progress, 100)}%` }}
                  />
                </div>
                <div id={`progress-${license.licenseId}-description`} className="sr-only">
                  {progress.isComplete ? 'CEU requirements completed' : `${Math.round(progress.progress)}% of CEU requirements completed`}
                </div>
              </div>

              {/* Special Requirements */}
              {progress.specialRequirements.length > 0 && (
                <div className="space-y-2" role="group" aria-label="Special license requirements">
                  {progress.specialRequirements.map((requirement, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm" role="listitem">
                      {requirement.includes('legal') ? (
                        <>
                          <div className={`w-2 h-2 rounded-full ${
                            progress.legalCEUs >= 20 ? 'bg-green-500' : 'bg-gray-300'
                          }`} role="img" aria-label={progress.legalCEUs >= 20 ? 'Requirement met' : 'Requirement not met'} />
                          <span className="text-gray-700">
                            Legal CEUs: {progress.legalCEUs.toFixed(1)} / 20.0
                          </span>
                          {progress.legalCEUs >= 20 && (
                            <CheckCircle className="w-4 h-4 text-green-500" aria-label="Legal CEU requirement completed" />
                          )}
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-blue-500" role="img" aria-label="Information indicator" />
                          <span className="text-gray-700">{requirement}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Audit Information */}
              {progress.auditCycle && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">Audit Cycle:</span> {progress.auditCycle} • 
                    <span className="ml-1">Keep records for audit compliance</span>
                  </p>
                </div>
              )}

              {/* Status Messages */}
              {progress.renewalStatus === 'expired' && (
                <Alert variant="error" className="text-sm" role="alert" aria-live="assertive">
                  License expired on {progress.renewalDate.format('MMM D, YYYY')}. Contact your state board immediately.
                </Alert>
              )}
              
              {progress.renewalStatus === 'urgent' && (
                <Alert variant="warning" className="text-sm" role="alert" aria-live="polite">
                  License expires in {progress.daysUntilRenewal} days. Renew immediately to avoid expiration.
                </Alert>
              )}
              
              {progress.renewalStatus === 'warning' && (
                <Alert variant="info" className="text-sm" role="status" aria-live="polite">
                  License expires in {progress.daysUntilRenewal} days. Plan your renewal soon.
                </Alert>
              )}
            </article>
          </Card>
        );
      })}
    </section>
  );
}